import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { SectionHeading, PreviewTag } from "@/components/kit";

const FLOW = ["TASK", "ESCROW", "ACTION", "EVIDENCE", "VERIFICATION", "PROOF", "USDC SETTLEMENT"];

const SPLIT = [
    { role: "AGENT", amount: "20 USDC", pct: 80 },
    { role: "VERIFIER", amount: "3 USDC", pct: 12 },
    { role: "PROTOCOL", amount: "2 USDC", pct: 8 },
];

export const SettlementFlow = () => (
    <section id="settlement" data-testid="settlement-flow" className="section relative">
        <div className="shell">
            <div className="grid gap-14 lg:grid-cols-2 lg:items-start">
                <Reveal>
                    <SectionHeading
                        index="06"
                        eyebrow="SETTLEMENT"
                        title={
                            <>
                                VERIFIED ACTIONS BECOME
                                <br />
                                <span className="text-[#1677FF]">ECONOMIC EVENTS.</span>
                            </>
                        }
                        description="Funds are placed in escrow when a task is created. When verification accepts the evidence, proof triggers settlement — natively in USDC on Arc."
                    />

                    <div className="mt-10" data-testid="settlement-chain">
                        {FLOW.map((f, i) => (
                            <div key={f} className="relative flex items-center gap-4 pb-5 last:pb-0">
                                {i < FLOW.length - 1 && (
                                    <span className="absolute left-[7px] top-4 h-full w-px bg-gradient-to-b from-[#1677FF]/60 to-white/[0.06]" aria-hidden="true" />
                                )}
                                <motion.span
                                    className={`relative z-10 h-[15px] w-[15px] shrink-0 rotate-45 border ${
                                        i === FLOW.length - 1 ? "border-[#31D8FF] bg-[#31D8FF]/20" : "border-[#1677FF] bg-[#040811]"
                                    }`}
                                    whileInView={{ scale: [0.6, 1] }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.09, duration: 0.4 }}
                                />
                                <span
                                    className={`font-mono text-[11px] uppercase tracking-[0.22em] ${
                                        i === FLOW.length - 1 ? "text-[#31D8FF]" : "text-ink"
                                    }`}
                                >
                                    {f}
                                </span>
                            </div>
                        ))}
                    </div>
                </Reveal>

                <Reveal delay={0.12}>
                    <div className="relative border border-white/[0.08] bg-[#0A1627] p-7 md:p-9">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <p className="label-tech">EXAMPLE SETTLEMENT SPLIT</p>
                            <PreviewTag>EXAMPLE ONLY</PreviewTag>
                        </div>

                        <div className="mt-8 flex items-baseline justify-between border-b border-white/[0.08] pb-6">
                            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-mute">REWARD</span>
                            <span className="font-display text-4xl font-bold tracking-tightest text-ink">25 USDC</span>
                        </div>

                        <div className="mt-8 space-y-6" data-testid="settlement-split">
                            {SPLIT.map((s, i) => (
                                <div key={s.role}>
                                    <div className="flex items-baseline justify-between">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mute">{s.role}</span>
                                        <span className="font-mono text-xs text-ink">{s.amount}</span>
                                    </div>
                                    <div className="mt-2 h-1.5 w-full bg-white/[0.06]">
                                        <motion.div
                                            className={`h-full ${i === 0 ? "bg-[#1677FF]" : i === 1 ? "bg-[#168BFF]" : "bg-[#31D8FF]"}`}
                                            initial={{ width: 0 }}
                                            whileInView={{ width: `${s.pct}%` }}
                                            viewport={{ once: true, amount: 0.6 }}
                                            transition={{ duration: 1, delay: 0.2 + i * 0.15, ease: [0.16, 1, 0.3, 1] }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <p className="mt-8 text-xs leading-relaxed text-mute/80">
                            Illustrative example only. Settlement economics are not finalized and will be published with the
                            protocol specification.
                        </p>
                    </div>
                </Reveal>
            </div>
        </div>
    </section>
);

export default SettlementFlow;
